import React, { useState, useCallback } from 'react';
import { uploadImport, finalizeImport } from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';
import { Upload, FileSpreadsheet, Check, AlertCircle, Loader2 } from 'lucide-react';

export default function ImportPage() {
  const [step, setStep] = useState('upload'); // upload, preview, finalizing, done
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await uploadImport(fd);
      setPreviewData(res.data);
      setStep('preview');
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleFinalize = async () => {
    setStep('finalizing');
    try {
      const res = await finalizeImport(previewData.batch_id);
      setResult(res.data);
      setStep('done');
    } catch (err) {
      setError(err.response?.data?.detail || 'Finalize failed');
      setStep('preview');
    }
  };

  const reset = () => {
    setStep('upload');
    setFile(null);
    setPreviewData(null);
    setResult(null);
    setError('');
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="import-page">
      <div>
        <h1 className="text-2xl font-bold text-boss-text">Import Invoices</h1>
        <p className="text-boss-text-muted mt-1">Upload Busy Sales Register Excel file to import invoices</p>
      </div>

      {/* Steps */}
      <div className="flex items-center gap-4">
        {['Upload', 'Preview', 'Finalize'].map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
              (i === 0 && step === 'upload') || (i === 1 && step === 'preview') || (i === 2 && (step === 'finalizing' || step === 'done'))
                ? 'bg-boss-violet text-white'
                : step === 'done' || (i === 0 && step !== 'upload')
                  ? 'bg-green-500 text-white'
                  : 'bg-boss-surface-3 text-boss-text-muted'
            }`}>
              {(step === 'done' && i < 2) || (step !== 'upload' && i === 0) ? <Check size={16} /> : i + 1}
            </div>
            <span className="text-sm font-medium text-boss-text">{s}</span>
            {i < 2 && <div className="w-12 h-px bg-boss-border" />}
          </div>
        ))}
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 rounded-xl text-red-700 text-sm">
          <AlertCircle size={16} /> {error}
        </div>
      )}

      {/* Upload Step */}
      {step === 'upload' && (
        <div className="bg-white rounded-xl p-8 shadow-sm border border-boss-border">
          <div className="border-2 border-dashed border-boss-border rounded-xl p-12 text-center">
            <Upload size={40} className="mx-auto text-boss-text-muted mb-4" />
            <h3 className="text-lg font-semibold text-boss-text mb-2">Upload Sales Register</h3>
            <p className="text-sm text-boss-text-muted mb-6">Select an Excel (.xlsx) file exported from Busy accounting software</p>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setFile(e.target.files[0])}
              className="hidden"
              id="file-upload"
              data-testid="import-file-input"
            />
            <label htmlFor="file-upload" className="inline-block px-6 py-2.5 bg-boss-violet text-white rounded-lg font-medium cursor-pointer hover:bg-boss-violet-2 transition-colors">
              Choose File
            </label>
            {file && (
              <div className="mt-4 flex items-center justify-center gap-2">
                <FileSpreadsheet size={16} className="text-green-600" />
                <span className="text-sm text-boss-text">{file.name}</span>
              </div>
            )}
          </div>
          {file && (
            <div className="mt-6 flex justify-end">
              <button
                onClick={handleUpload}
                disabled={uploading}
                className="px-6 py-2.5 bg-boss-violet text-white rounded-lg font-medium hover:bg-boss-violet-2 disabled:opacity-50 flex items-center gap-2"
                data-testid="import-upload-btn"
              >
                {uploading && <Loader2 size={16} className="animate-spin" />}
                {uploading ? 'Processing...' : 'Upload & Parse'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Preview Step */}
      {step === 'preview' && previewData && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-boss-border">
            <h3 className="text-lg font-semibold text-boss-text mb-4">Import Preview</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-boss-surface-2 p-3 rounded-lg">
                <div className="text-xl font-bold text-boss-text">{previewData.total_rows}</div>
                <div className="text-xs text-boss-text-muted">Total Rows</div>
              </div>
              {Object.entries(previewData.series_breakdown || {}).map(([s, c]) => (
                <div key={s} className="bg-boss-surface-2 p-3 rounded-lg">
                  <div className="text-xl font-bold text-boss-text">{c}</div>
                  <div className="text-xs text-boss-text-muted">Series {s}</div>
                </div>
              ))}
              <div className="bg-boss-surface-2 p-3 rounded-lg">
                <div className="text-sm font-medium text-boss-text">{previewData.date_range || '---'}</div>
                <div className="text-xs text-boss-text-muted">Date Range</div>
              </div>
            </div>

            {/* Preview Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="preview-table">
                <thead>
                  <tr className="border-b border-boss-border">
                    <th className="text-left py-2 px-3 text-boss-text-muted font-medium">Bill No.</th>
                    <th className="text-left py-2 px-3 text-boss-text-muted font-medium">Party</th>
                    <th className="text-left py-2 px-3 text-boss-text-muted font-medium">Salesman</th>
                    <th className="text-left py-2 px-3 text-boss-text-muted font-medium">Transport</th>
                    <th className="text-left py-2 px-3 text-boss-text-muted font-medium">Station</th>
                    <th className="text-right py-2 px-3 text-boss-text-muted font-medium">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(previewData.preview || []).slice(0, 20).map((inv, i) => (
                    <tr key={i} className="border-b border-boss-border/50 hover:bg-boss-surface-2">
                      <td className="py-2 px-3 font-mono text-xs">{inv.bill_no_full}</td>
                      <td className="py-2 px-3 truncate max-w-[160px]">{inv.party_name}</td>
                      <td className="py-2 px-3">{inv.salesman_name}</td>
                      <td className="py-2 px-3">{inv.transport}</td>
                      <td className="py-2 px-3">{inv.station}</td>
                      <td className="py-2 px-3 text-right font-mono">{formatCurrency(inv.amount_grand_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex gap-4 justify-end">
            <button onClick={reset} className="px-6 py-2.5 border border-boss-border rounded-lg text-sm font-medium hover:bg-boss-surface-2">Cancel</button>
            <button
              onClick={handleFinalize}
              className="px-6 py-2.5 bg-boss-violet text-white rounded-lg font-medium hover:bg-boss-violet-2 flex items-center gap-2"
              data-testid="import-finalize-btn"
            >
              <Check size={16} /> Finalize Import
            </button>
          </div>
        </div>
      )}

      {/* Finalizing */}
      {step === 'finalizing' && (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-boss-border text-center">
          <Loader2 size={40} className="mx-auto animate-spin text-boss-violet mb-4" />
          <h3 className="text-lg font-semibold text-boss-text">Importing invoices...</h3>
          <p className="text-sm text-boss-text-muted">Creating records, detecting duplicates, checking sequence gaps</p>
        </div>
      )}

      {/* Done */}
      {step === 'done' && result && (
        <div className="bg-white rounded-xl p-8 shadow-sm border border-boss-border">
          <div className="text-center mb-6">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <Check size={32} className="text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-boss-text">Import Complete!</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-green-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-green-700">{result.created}</div>
              <div className="text-xs text-green-600">Created</div>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-700">{result.updated}</div>
              <div className="text-xs text-blue-600">Updated</div>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-yellow-700">{result.duplicates}</div>
              <div className="text-xs text-yellow-600">Duplicates</div>
            </div>
            <div className="bg-red-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-red-700">{result.errors}</div>
              <div className="text-xs text-red-600">Errors</div>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-orange-700">{result.gaps_found}</div>
              <div className="text-xs text-orange-600">Gaps Found</div>
            </div>
          </div>
          <div className="mt-6 text-center">
            <button onClick={reset} className="px-6 py-2.5 bg-boss-violet text-white rounded-lg font-medium hover:bg-boss-violet-2">Import Another</button>
          </div>
        </div>
      )}
    </div>
  );
}
